import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import dektalMascot from '../assets/dektal-mascot-final.png';
import DektalChatPanel from './DektalChatPanel';
import DektalNotificationsPanel from './DektalNotificationsPanel';
import {
  getDektalNotifications,
  getDektalNotificationsUnreadCount,
} from '../services/dektalNotificationApi';

function normalizeRoutePath(route) {
  const value = String(route || '').trim();
  if (!value) return '/dashboard';

  const mapping = {
    '/appointments': '/dashboard',
    '/payments': '/emi-reminders',
    '/business-reports': '/reports',
    '/revenue-report': '/reports',
    '/assistant': '/dashboard',
  };

  return mapping[value] || value;
}

function navigateWithSafeParams(navigate, route, params) {
  const safeRoute = normalizeRoutePath(route);
  const normalizedParams = params && typeof params === 'object' ? params : null;

  if (!normalizedParams || Object.keys(normalizedParams).length === 0) {
    navigate(safeRoute);
    return;
  }

  const urlParams = new URLSearchParams();
  Object.entries(normalizedParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'object') return;
    urlParams.set(key, String(value));
  });

  const query = urlParams.toString();
  if (!query) {
    navigate(safeRoute, { state: normalizedParams });
    return;
  }
  navigate(`${safeRoute}?${query}`, { state: normalizedParams });
}

function getDektalPriorityWeight(priority) {
  const normalized = String(priority || 'low').toLowerCase();
  if (normalized === 'critical') return 4;
  if (normalized === 'high') return 3;
  if (normalized === 'medium') return 2;
  return 1;
}

function pickHighestPriorityNotification(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  return [...rows].sort((left, right) => {
    const priorityDiff = getDektalPriorityWeight(right.priority) - getDektalPriorityWeight(left.priority);
    if (priorityDiff !== 0) return priorityDiff;

    return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
  })[0];
}

function getSafeSpeechMessage(notification) {
  if (notification?.requiresFinanceUnlock) {
    return 'Dektal found finance-related alerts. Unlock Finance Lock to view details.';
  }

  return notification?.message || '';
}

function buildSpeechBubbleFromNotification(notification, unreadCount) {
  const safeMessage = getSafeSpeechMessage(notification);
  if (safeMessage) {
    return {
      id: `notification-${notification.id || notification.createdAt || Date.now()}`,
      title: notification.requiresFinanceUnlock ? 'Dektal says' : notification.title || 'Dektal says',
      message: safeMessage,
      actionButton: notification.actionRoute
        ? {
            label: notification.actionLabel || 'Open',
            route: notification.actionRoute,
            params: notification.actionParamsJson || null,
          }
        : null,
      notification,
    };
  }

  if (unreadCount > 0) {
    return {
      id: `unread-${unreadCount}`,
      title: 'Dektal says',
      message: unreadCount === 1
        ? 'You have one clinic notification waiting.'
        : `You have ${unreadCount} clinic notifications waiting.`,
      actionButton: null,
      notification: null,
    };
  }

  return null;
}

export default function DektalAssistantWidget() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [isHidden, setIsHidden] = useState(() => {
    const stored = window.sessionStorage.getItem('dektalAssistantHidden');
    return stored === 'true';
  });
  const [stagePosition, setStagePosition] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem('dektalAssistantPosition');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingUnread, setLoadingUnread] = useState(false);
  const [replyPulse, setReplyPulse] = useState(false);
  const [speechBubble, setSpeechBubble] = useState(null);
  const [dismissedSpeechId, setDismissedSpeechId] = useState('');
  const dragRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const unreadBubbleVisible = unreadCount > 0;

  useEffect(() => {
    if (stagePosition) {
      window.sessionStorage.setItem('dektalAssistantPosition', JSON.stringify(stagePosition));
    }
  }, [stagePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => panelRef.current?.focus());
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isOpen]);

  const fetchNotificationSummary = useCallback(async ({ quiet = true } = {}) => {
    if (!quiet) {
      setLoadingUnread(true);
    }

    try {
      const countPayload = await getDektalNotificationsUnreadCount();
      const nextUnreadCount = Number(countPayload?.unreadCount || 0);
      setUnreadCount(nextUnreadCount);

      if (nextUnreadCount <= 0 || isOpen) {
        setSpeechBubble(null);
        return;
      }

      let latestNotification = null;
      try {
        const notificationPayload = await getDektalNotifications({
          includeRead: false,
          includeDismissed: false,
          includeArchived: false,
          limit: 8,
        });
        const rows = Array.isArray(notificationPayload?.rows) ? notificationPayload.rows : [];
        latestNotification = pickHighestPriorityNotification(rows);
      } catch (notificationError) {
        const notificationStatus = notificationError?.response?.status;
        if (!quiet && notificationStatus !== 403) {
          toast.error(notificationError?.response?.data?.error || 'Unable to load Dektal notification preview.');
        }
      }

      const nextSpeechBubble = buildSpeechBubbleFromNotification(latestNotification, nextUnreadCount);
      if (nextSpeechBubble && nextSpeechBubble.id !== dismissedSpeechId) {
        setSpeechBubble(nextSpeechBubble);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (!quiet && status !== 403) {
        toast.error(error?.response?.data?.error || 'Unable to load Dektal notifications.');
      }
    } finally {
      setLoadingUnread(false);
    }
  }, [dismissedSpeechId, isOpen]);

  useEffect(() => {
    fetchNotificationSummary({ quiet: true });
    const interval = setInterval(() => {
      fetchNotificationSummary({ quiet: true });
    }, 45000);
    return () => clearInterval(interval);
  }, [fetchNotificationSummary]);

  useEffect(() => {
    const openFromDashboard = () => handleOpenPanel();
    const restoreFromNavigation = () => {
      window.sessionStorage.removeItem('dektalAssistantHidden');
      setIsHidden(false);
    };
    window.addEventListener('dektal-assistant-open', openFromDashboard);
    window.addEventListener('dektal-assistant-restore', restoreFromNavigation);
    return () => {
      window.removeEventListener('dektal-assistant-open', openFromDashboard);
      window.removeEventListener('dektal-assistant-restore', restoreFromNavigation);
    };
  });

  useEffect(() => {
    if (!speechBubble || isOpen) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setDismissedSpeechId(speechBubble.id || '');
      setSpeechBubble(null);
    }, 30000);

    return () => clearTimeout(timeout);
  }, [isOpen, speechBubble]);

  const handleNavigateAction = async (actionButton, context) => {
    if (!actionButton?.route) {
      return;
    }
    setReplyPulse(true);
    setTimeout(() => setReplyPulse(false), 850);
    navigateWithSafeParams(navigate, actionButton.route, actionButton.params);

    if (context?.source === 'notification') {
      setUnreadCount((current) => Math.max(0, current - 1));
      setSpeechBubble(null);
    }
  };

  const handleOpenPanel = (tab = 'chat') => {
    setActiveTab(tab);
    setIsOpen(true);
    setSpeechBubble(null);
    setReplyPulse(true);
    setTimeout(() => setReplyPulse(false), 850);
  };

  const handleClosePanel = () => {
    setIsOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const handleHideWidget = () => {
    setIsOpen(false);
    setSpeechBubble(null);
    setIsHidden(true);
    window.sessionStorage.setItem('dektalAssistantHidden', 'true');
  };

  const handleDismissSpeechBubble = (event) => {
    event.stopPropagation();
    if (speechBubble?.id) {
      setDismissedSpeechId(speechBubble.id);
    }
    setSpeechBubble(null);
  };

  const handlePointerDown = (event) => {
    if (isOpen || event.button > 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      didDrag: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const dragState = dragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const distance = Math.abs(event.clientX - dragState.startX) + Math.abs(event.clientY - dragState.startY);
    if (distance < 6 && !dragState.didDrag) return;

    dragState.didDrag = true;
    const width = 112;
    const height = 116;
    const nextLeft = Math.min(
      Math.max(12, event.clientX - dragState.offsetX),
      Math.max(12, window.innerWidth - width - 12)
    );
    const nextTop = Math.min(
      Math.max(12, event.clientY - dragState.offsetY),
      Math.max(12, window.innerHeight - height - 12)
    );

    setStagePosition({
      left: Math.round(nextLeft),
      top: Math.round(nextTop),
    });
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      window.setTimeout(() => {
        dragRef.current = null;
      }, 0);
    }
  };

  const handleMascotClick = () => {
    if (dragRef.current?.didDrag) return;
    handleOpenPanel();
  };

  const stageStyle = stagePosition && !isOpen
    ? {
        left: `${stagePosition.left}px`,
        top: `${stagePosition.top}px`,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined;

  return (
    <>
        <section
          ref={panelRef}
          tabIndex={-1}
          className={isHidden
            ? 'hidden'
            : `dektal-panel dektal-panel-position fixed z-[90] flex h-[min(78dvh,690px)] flex-col overflow-hidden rounded-[30px] border border-white/80 bg-white/95 shadow-[0_26px_70px_rgba(15,23,42,0.24)] backdrop-blur-xl transition-opacity ${isOpen ? '' : 'invisible pointer-events-none opacity-0'}`}
          aria-label="Dektal assistant communication panel"
          aria-hidden={!isOpen}
        >
          <header className="relative overflow-hidden border-b border-slate-100 px-4 py-3">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_0%,rgba(34,211,238,0.18),transparent_30%),linear-gradient(120deg,rgba(248,250,252,0.95),rgba(239,246,255,0.9))]" />
            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-cyan-100 bg-white/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-700">
                  <Sparkles className="h-3 w-3" />
                  Dektal
                </div>
                <h2 className="text-lg font-bold tracking-tight text-slate-950">Ask Dektal</h2>
                <p className="text-xs leading-5 text-slate-500">
                  Type naturally. Dektal can answer clinic questions and simple how-to questions.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleClosePanel}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-slate-200 bg-white/90 p-2 text-slate-600 transition hover:border-rose-200 hover:text-rose-600"
                  title="Close Ask Dektal"
                  aria-label="Close Ask Dektal"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </header>

          <div className="shrink-0 border-b border-slate-200 bg-white px-3 pt-1" role="tablist" aria-label="Ask Dektal panel sections">
            <button type="button" role="tab" aria-selected={activeTab === 'chat'} aria-controls="dektal-chat-panel" onClick={() => setActiveTab('chat')} className={`min-h-11 rounded-t-xl px-4 text-sm font-semibold ${activeTab === 'chat' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-slate-500'}`}>Ask Dektal</button>
            <button type="button" role="tab" aria-selected={activeTab === 'notifications'} aria-controls="dektal-notifications-panel" onClick={() => setActiveTab('notifications')} className={`min-h-11 rounded-t-xl px-4 text-sm font-semibold ${activeTab === 'notifications' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-slate-500'}`}>Notifications{unreadCount ? ` (${unreadCount})` : ''}</button>
          </div>
          <div id="dektal-chat-panel" role="tabpanel" hidden={activeTab !== 'chat'} className="flex-1 min-h-0">
            <DektalChatPanel onNavigateAction={handleNavigateAction} />
          </div>
          <div id="dektal-notifications-panel" role="tabpanel" hidden={activeTab !== 'notifications'} className="flex-1 min-h-0">
            <DektalNotificationsPanel active={isOpen && activeTab === 'notifications'} onUnreadChange={setUnreadCount} onNavigateAction={handleNavigateAction} />
          </div>
        </section>

      {!isHidden ? <div
        className={`dektal-stage dektal-stage-position fixed z-[89] flex flex-row-reverse items-end gap-3 ${isOpen ? 'dektal-stage-open' : ''}`}
        style={stageStyle}
      >
        <button
          ref={triggerRef}
          type="button"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={handleMascotClick}
          className={`dektal-character-button dektal-character-float relative flex items-end justify-start transition hover:scale-[1.04] focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 ${
            replyPulse ? 'dektal-reply-pulse' : ''
          } ${unreadBubbleVisible ? 'dektal-character-has-unread' : ''}`}
          title="Open Dektal assistant"
          aria-label="Open Dektal assistant"
        >
          <img
            src={dektalMascot}
            alt="Dektal mascot"
            className={`dektal-mascot-full h-auto object-contain ${isOpen ? 'w-[142px] md:w-[172px]' : 'w-[68px] md:w-[84px]'}`}
          />
          <span className="sr-only">Open Dektal assistant</span>
          {unreadBubbleVisible ? (
            <span className="dektal-unread-dot absolute right-1 top-1 inline-flex h-4 w-4 rounded-full bg-blue-600 shadow-[0_0_0_4px_rgba(219,234,254,0.9)] md:h-4 md:w-4">
              <span className="sr-only">{unreadCount} unread Dektal notifications</span>
            </span>
          ) : null}
          {loadingUnread ? (
            <span className="absolute bottom-1 left-1 rounded-full bg-slate-950/85 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              ...
            </span>
          ) : null}
        </button>

        {!isOpen ? (
          <button
            type="button"
            onClick={handleHideWidget}
            className="mb-2 inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-600 shadow-lg transition hover:border-rose-200 hover:text-rose-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
            title="Hide Ask Dektal"
            aria-label="Hide Ask Dektal"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}

        {!isOpen && speechBubble ? (
          <div className="dektal-speech-bubble max-w-[min(310px,calc(100vw-8rem))]" role="status">
            <button
              type="button"
              onClick={handleDismissSpeechBubble}
              className="absolute right-2 top-2 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              title="Dismiss Dektal speech"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <p className="pr-5 text-[11px] font-bold uppercase tracking-[0.16em] text-cyan-700">
              {speechBubble.title || 'Dektal says'}
            </p>
            <p className="mt-1 text-sm leading-5 text-slate-700">{speechBubble.message}</p>
            {speechBubble.actionButton ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleNavigateAction(speechBubble.actionButton, {
                      source: 'notification',
                      notification: speechBubble.notification,
                    });
                  }}
                  className="rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700"
                >
                  {speechBubble.actionButton.label || 'Open'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div> : null}
    </>
  );
}
